import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { activePb, pb as pbAdmin, pbPortal, pbEngineer, onScopeChange, keepSessionAlive, clearFileToken } from '../lib/pb';
import { applyTenantBranding } from '../theme/tokens';
import * as mappers from './mappers';

// =====================================================================
// DataProvider — live PocketBase backing for the app.
//
// Keeps the EXACT useData() contract the screens were built against
// ({ data, add, update, remove, updateCollection }) with schema-shaped
// records mapped to/from the UI shapes by ./mappers.js.
//
// - Loads all collections scoped by the authenticated user's rules
//   (agency_admin/staff see their tenant; clients see only their own).
// - Subscribes to `messages` realtime so both the admin inbox and the
//   portal chat update live (frontend.md §6).
// - Reloads automatically when the auth store changes (login/logout,
//   portal session resolution).
// =====================================================================

const DataContext = createContext(null);

function emptyData() {
  return {
    tenant: mappers.DEFAULT_TENANT,
    settings: mappers.defaultSettings(),
    clients: [],
    jobs: [],
    properties: [],
    engineers: [],
    invoices: [],
    payments: [],
    keyDates: [],
    messages: [],
    documents: [],
    notification_log: [],
    team: [],
    notes: [],
    quotes: [],
    certificates: [],
    reviewRequests: [],
    // Public marketing sign-up leads (migration 025) — agency_admin only.
    signups: [],
    pricingCatalog: [],
    portalVisits: [],
    // Line item templates (task 1.5) — per-tenant reusable line-item sets.
    lineItemTemplates: [],
    // Client document acknowledgements (tracking_tasks.md TRK-3.1/TRK-4.1)
    // — small per-client set, loaded for client + agency sessions.
    documentAcks: [],
    // Engineer portal — rule-scoped reference lists loaded for engineer
    // sessions (parts_db / faults_db / engineer_docs list rules already
    // include the engineer role; audit 002 §016).
    parts: [],
    faults: [],
    engineerDocs: [],
    // Admin review data (update_tasks.md §7) — loaded for agency sessions:
    // engineer job sheets, expenses, ETA updates and location pings.
    engineerJobSheets: [],
    expenses: [],
    etaUpdates: [],
    engineerLocations: [],
  };
}

function tempPassword(length = 12) {
  const alphabet = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let out = '';
  for (let i = 0; i < length; i += 1) out += alphabet[Math.floor(Math.random() * alphabet.length)];
  return out;
}

// data:URL (e.g. device-camera capture) → File for PB file upload.
function dataUrlToFile(dataUrl, filename) {
  try {
    if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) return null;
    const [head, b64] = dataUrl.split(',');
    const mime = /data:([^;]+)/.exec(head)?.[1] || 'image/jpeg';
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], filename, { type: mime });
  } catch {
    return null;
  }
}

export function DataProvider({ children }) {
  const [data, setData] = useState(emptyData);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const tenantRef = useRef(mappers.DEFAULT_TENANT);
  const [sessionKey, setSessionKey] = useState(0);

  const applyBranding = useCallback((tenantUi) => {
    applyTenantBranding({
      primary: tenantUi.primary_color || '#173e36',
      primaryStrong: tenantUi.primary_color || '#173e36',
      accent: tenantUi.accent_color || '#dfff73',
    });
  }, []);

  const load = useCallback(async () => {
    const pb = activePb();
    if (!pb.authStore.token || !pb.authStore.record) {
      setData(emptyData());
      tenantRef.current = mappers.DEFAULT_TENANT;
      setReady(true);
      return;
    }

    setReady(false);
    try {
      const role = pb.authStore.record.role;
      // Engineers have their own collections (engineer_jobs etc.) and no
      // read access to clients/jobs/invoices/… — the Promise.all below
      // would reject on the first 403 and leave the engineer portal
      // empty. Load only the tenant (name/branding) for engineer sessions;
      // engineer screens fetch their own data via pbEngineer directly.
      if (role === 'engineer') {
        // The engineer's OWN tenant (tenants.viewRule: id = auth.tenant_id —
        // getOne is rule-scoped and safe). Fallback to the first tenant only
        // for legacy engineer accounts with no tenant_id. tenantList[0] was
        // WRONG with 2+ tenants (engineer of tenant #2 got tenant #1 →
        // EngineerAuthGate's mismatch check denied access).
        const engineerTenantId = pb.authStore.record.tenant_id;
        let tenantRec = null;
        if (engineerTenantId) {
          try {
            tenantRec = await pb.collection('tenants').getOne(engineerTenantId);
          } catch (err) {
            console.warn('[data] engineer tenant fetch failed', err?.message || err);
          }
        }
        if (!tenantRec) {
          const tenantList = await pb.collection('tenants').getFullList({ sort: 'created' });
          tenantRec = tenantList[0] || null;
        }
        const tenantUi = tenantRec ? mappers.mapTenant(tenantRec) : mappers.DEFAULT_TENANT;
        tenantRef.current = tenantUi;

        // Reference lists the engineer role can read (list rules already
        // include `role = "engineer" && tenant_id = auth.tenant_id` — audit
        // 002/016). Loaded so Parts/Faults/Files screens read from useData()
        // like every other screen; each getFullList is rule-scoped and
        // individually caught so one 403 can't blank the whole session.
        const [parts, faults, engineerDocs] = await Promise.all([
          pb.collection('parts_db').getFullList({ sort: 'name' }).catch(() => []),
          pb.collection('faults_db').getFullList({ sort: 'fault_code' }).catch(() => []),
          pb.collection('engineer_docs').getFullList({ sort: '-created' }).catch(() => []),
        ]);

        setData((current) => ({
          ...emptyData(),
          tenant: tenantUi,
          settings: tenantRec ? mappers.buildSettings(tenantUi) : mappers.defaultSettings(),
          parts: parts.map(mappers.mapPart),
          faults: faults.map(mappers.mapFault),
          engineerDocs: engineerDocs.map(mappers.mapEngineerDoc),
        }));
        applyBranding(tenantUi);
        setError(null);
        setReady(true);
        return;
      }

      const [tenantList, clients, jobs, properties, invoices, payments, keyDates, messages, documents, notes, quotes, certificates, documentAcks] = await Promise.all([
        pb.collection('tenants').getFullList({ sort: 'created' }),
        pb.collection('clients').getFullList({ sort: 'name' }),
        pb.collection('jobs').getFullList({ sort: '-scheduled_start,-date_completed', expand: 'engineer,property_id' }),
        // Properties (migration 021): client + agency roles both read their
        // own scope via list rules; client_name resolved for admin lists.
        pb.collection('properties').getFullList({ sort: 'name', expand: 'client_id' }).catch(() => []),
        pb.collection('invoices').getFullList({ expand: 'job_id' }),
        pb.collection('payments').getFullList(),
        pb.collection('key_dates').getFullList({ sort: 'due_date' }),
        pb.collection('messages').getFullList({ sort: 'created' }),
        pb.collection('documents').getFullList(),
        pb.collection('notes').getFullList({ sort: '-created' }).catch(() => []),
        pb.collection('quotes').getFullList({ sort: '-created' }).catch(() => []),
        pb.collection('certificates').getFullList({ sort: 'expiry_date' }).catch(() => []),
        // Client + agency list rules cover both roles; rule-scoped per row.
        pb.collection('document_acknowledgements').getFullList({ sort: '-created' }).catch(() => []),
      ]);

      let notificationLog = [];
      let team = [];
      let engineers = [];
      let reviewRequests = [];
      let signups = [];
      let lineItemTemplates = [];
      let pricingCatalog = [];
      let portalVisits = [];
      let engineerJobSheets = [];
      let engineerDocs = [];
      let parts = [];
      let faults = [];
      let expenses = [];
      let etaUpdates = [];
      let engineerLocations = [];
      if (role === 'agency_admin' || role === 'agency_staff') {
        // Engineer-review collections (update_tasks.md §7): agency roles
        // have full list rules on all of these; agency_staff is tenant-
        // scoped by the rule itself. Each fetch is individually caught so
        // one 403 can't blank the whole admin session.
        [notificationLog, team, engineers, reviewRequests, signups, lineItemTemplates, pricingCatalog, portalVisits, engineerJobSheets, engineerDocs, parts, faults, expenses, etaUpdates, engineerLocations] = await Promise.all([
          pb.collection('notification_log').getFullList({ sort: '-sent_at' }),
          pb.collection('accounts').getFullList(),
          pb.collection('engineers').getFullList({ sort: 'name' }).catch(() => []),
          pb.collection('review_requests').getFullList({ sort: '-sent_at' }).catch(() => []),
          // Marketing sign-up leads (migration 025) — list rule is
          // agency_admin only; staff sessions get a 403 → [].
          pb.collection('signups').getFullList({ sort: '-created' }).catch(() => []),
          // Line item templates (task 1.5) — agency-only, per-tenant.
          pb.collection('line_item_templates').getFullList({ sort: 'sort,name' }).catch(() => []),
          pb.collection('pricing_catalog').getFullList({ sort: 'category,item_name' }).catch(() => []),
          pb.collection('portal_visits').getFullList({ sort: '-visited_at' }).catch(() => []),
          pb.collection('engineer_job_sheets').getFullList({ sort: '-created', expand: 'engineer_job_id,client_id' }).catch(() => []),
          pb.collection('engineer_docs').getFullList({ sort: '-created' }).catch(() => []),
          pb.collection('parts_db').getFullList({ sort: 'name' }).catch(() => []),
          pb.collection('faults_db').getFullList({ sort: 'fault_code' }).catch(() => []),
          pb.collection('expenses').getFullList({ sort: '-date,-created' }).catch(() => []),
          pb.collection('eta_updates').getFullList({ sort: '-created_at' }).catch(() => []),
          pb.collection('engineer_locations').getFullList({ sort: '-timestamp' }).catch(() => []),
        ]);
      }

      const tenantRec = tenantList[0] || null;
      const tenantUi = tenantRec ? mappers.mapTenant(tenantRec) : mappers.DEFAULT_TENANT;
      tenantRef.current = tenantUi;

      const invoicesById = Object.fromEntries(invoices.map((rec) => [rec.id, rec]));
      const jobsById = Object.fromEntries(jobs.map((rec) => [rec.id, rec]));

      setData({
        tenant: tenantUi,
        settings: tenantRec ? mappers.buildSettings(tenantUi) : mappers.defaultSettings(),
        clients: clients.map(mappers.mapClient),
        jobs: jobs.map(mappers.mapJob),
        properties: properties.map(mappers.mapProperty),
        invoices: invoices.map(mappers.mapInvoice),
        payments: payments.map((rec) => mappers.mapPayment(rec, invoicesById)),
        keyDates: keyDates.map(mappers.mapKeyDate),
        messages: messages.map(mappers.mapMessage),
        documents: documents.map(mappers.mapDocument),
        notification_log: notificationLog.map(mappers.mapNotification),
        team: team.filter((rec) => rec.role !== 'client').map(mappers.mapTeamMember),
        engineers: engineers.map(mappers.mapEngineer),
        notes: notes.map(mappers.mapNote),
        quotes: quotes.map(mappers.mapQuote),
        certificates: certificates.map(mappers.mapCertificate),
        reviewRequests: reviewRequests.map((rec) => mappers.mapReviewRequest(rec, jobsById)),
        signups: signups.map(mappers.mapSignup),
        lineItemTemplates: lineItemTemplates.map(mappers.mapLineItemTemplate),
        pricingCatalog: pricingCatalog.map(mappers.mapPricingItem),
        portalVisits: portalVisits.map(mappers.mapPortalVisit),
        documentAcks: documentAcks.map(mappers.mapAcknowledgement),
        // Engineer-review data (update_tasks.md §7) — admin CRUD + review.
        engineerJobSheets: engineerJobSheets.map(mappers.mapEngineerJobSheet),
        engineerDocs: engineerDocs.map(mappers.mapEngineerDoc),
        parts: parts.map(mappers.mapPart),
        faults: faults.map(mappers.mapFault),
        expenses: expenses.map(mappers.mapExpense),
        etaUpdates: etaUpdates.map(mappers.mapEtaUpdate),
        engineerLocations: engineerLocations.map(mappers.mapEngineerLocation),
      });

      applyBranding(tenantUi);
      setError(null);
    } catch (err) {
      console.error('[data] load failed', err);
      setError(err.message || 'Failed to load data');
      setData(emptyData());
    } finally {
      setReady(true);
    }
  }, [sessionKey, applyBranding]);

  useEffect(() => {
    load();
  }, [load]);

  // Silent session keep-alive: refresh the token once on mount (after the
  // first load starts) so active users slide the 30-day window forward.
  // authRefresh re-saves the store internally → onChange fires → sessionKey
  // bumps → data reloads with the fresh record. If the token is dead the
  // store is cleared and the login screens appear.
  useEffect(() => {
    keepSessionAlive();
  }, []);

  // Reload on auth changes (login, logout, portal session resolution).
  // Marking `ready` false HERE (synchronously, not inside the async load)
  // is what lets gates render a skeleton instead of the stale empty data
  // from the previous session — without it, PortalAuthGate/AdminShell can
  // render with `ready=true` + empty data for one frame and crash on
  // `data.clients[0].initials` etc. (caught in production).
  useEffect(() => {
    // ALL THREE stores matter: switching routes (admin ↔ portal ↔ engineer)
    // flips the ACTIVE client, and ANY store's login/logout/logout-expiry
    // must reload whatever the user is currently looking at. pbEngineer was
    // missing here (incident 2026-08-22): engineer login fired no reload,
    // EngineerAuthGate compared the session's tenant_id against the stale
    // DEFAULT_TENANT (empty id) → "does not belong to this tenant".
    const bump = () => {
      setReady(false);
      setSessionKey((key) => key + 1);
      // Drop cached protected-file tokens on ANY auth change — they're
      // account-scoped (PB file tokens are issued per session) and a stale
      // token from the previous account could briefly serve one client's
      // files to another's UI after a session swap.
      clearFileToken(pbAdmin);
      clearFileToken(pbPortal);
      clearFileToken(pbEngineer);
    };
    const unbindScope = onScopeChange(bump);
    const unbindAdmin = pbAdmin.authStore.onChange(bump);
    const unbindPortal = pbPortal.authStore.onChange(bump);
    const unbindEngineer = pbEngineer.authStore.onChange(bump);
    return () => {
      unbindScope?.();
      unbindAdmin?.();
      unbindPortal?.();
      unbindEngineer?.();
    };
  }, []);

  // Realtime messages — both shells update live.
  useEffect(() => {
    if (!ready || !activePb().authStore.token) return undefined;

    const pb = activePb();

    let active = true;
    let unsubscribe = null;

    // '*' (all records the authed user may view per list rules) — an empty
    // filter string is NOT a valid topic and rejects with "Missing topic".
    const handler = (event) => {
      if (!active || (event.action !== 'create' && event.action !== 'update')) return;
      const ui = mappers.mapMessage(event.record);
      setData((current) => {
        // create: skip if the optimistic add() already appended this record
        if (event.action === 'create' && current.messages.some((item) => item.id === ui.id)) {
          return current;
        }
        if (event.action === 'create') {
          return { ...current, messages: [...current.messages, ui] };
        }
        return { ...current, messages: current.messages.map((item) => (item.id === ui.id ? ui : item)) };
      });
    };

    // SDK 0.28: subscribe() is ASYNC and resolves to an UnsubscribeFunc —
    // calling its raw return value threw "S is not a function" and
    // unmounted the React tree (caught in production). Keep the resolved
    // handle; never call the promise itself.
    pb.collection('messages').subscribe('*', handler)
      .then((fn) => {
        if (!active) {
          // Effect already cleaned up — release immediately.
          Promise.resolve(fn()).catch(() => {});
          return;
        }
        unsubscribe = fn;
      })
      .catch((err) => console.warn('[data] realtime subscribe failed', err?.message || err));

    return () => {
      active = false;
      if (unsubscribe) {
        Promise.resolve(unsubscribe()).catch(() => {});
      } else {
        // Subscribe never resolved (or rejected) — clear any stragglers.
        Promise.resolve(pb.collection('messages').unsubscribe()).catch(() => {});
      }
    };
  }, [ready, sessionKey]);

  // Realtime engineer-review collections — keeps the admin engineer panels
  // live (update_tasks.md §7 "always alive and fetching": an engineer
  // completing a sheet, logging an expense or an ETA flip shows up in the
  // admin screens without a manual refresh). Engineers also get live
  // parts/faults/engineer_docs so admin CRUD lands immediately. Same
  // SDK-0.28 async-subscribe pattern as messages (incident #3).
  useEffect(() => {
    if (!ready || !activePb().authStore.token) return undefined;

    const pb = activePb();
    const role = pb.authStore.record?.role || '';
    const isAgency = role === 'agency_admin' || role === 'agency_staff';
    // [collection, dataKey, mapper]
    const targets = isAgency
      ? [
          ['engineer_job_sheets', 'engineerJobSheets', mappers.mapEngineerJobSheet],
          ['engineer_docs', 'engineerDocs', mappers.mapEngineerDoc],
          ['parts_db', 'parts', mappers.mapPart],
          ['faults_db', 'faults', mappers.mapFault],
          ['expenses', 'expenses', mappers.mapExpense],
          ['eta_updates', 'etaUpdates', mappers.mapEtaUpdate],
          ['engineer_locations', 'engineerLocations', mappers.mapEngineerLocation],
        ]
      : role === 'engineer'
        ? [
            ['parts_db', 'parts', mappers.mapPart],
            ['faults_db', 'faults', mappers.mapFault],
            ['engineer_docs', 'engineerDocs', mappers.mapEngineerDoc],
          ]
        : [];

    if (!targets.length) return undefined;

    let active = true;
    const unsubs = [];

    targets.forEach(([collection, dataKey, map]) => {
      const handler = (event) => {
        if (!active || (event.action !== 'create' && event.action !== 'update')) return;
        const ui = map(event.record);
        setData((current) => {
          const existing = current[dataKey].find((item) => item.id === ui.id);
          // Realtime payloads carry no expand relations — preserve the
          // previously resolved client/engineer-job names on updates.
          let merged = ui;
          if (dataKey === 'engineerJobSheets' && existing) {
            merged = {
              ...ui,
              client: ui.client || existing.client || '',
              engineer_job: ui.engineer_job || existing.engineer_job || null,
            };
          }
          if (event.action === 'create') {
            if (current[dataKey].some((item) => item.id === ui.id)) return current;
            return { ...current, [dataKey]: [...current[dataKey], merged] };
          }
          return { ...current, [dataKey]: current[dataKey].map((item) => (item.id === ui.id ? merged : item)) };
        });
      };
      pb.collection(collection).subscribe('*', handler)
        .then((fn) => {
          if (!active) {
            Promise.resolve(fn()).catch(() => {});
            return;
          }
          unsubs.push(fn);
        })
        .catch((err) => console.warn('[data] realtime subscribe failed', collection, err?.message || err));
    });

    return () => {
      active = false;
      unsubs.forEach((fn) => Promise.resolve(fn()).catch(() => {}));
    };
  }, [ready, sessionKey]);

  // ------------------------------------------------------------------
  // Writes — each returns the created/updated UI record
  // ------------------------------------------------------------------

  const add = useCallback(async (collection, value) => {
    const pb = activePb();
    const tenantId = tenantRef.current.id;
    let record;
    let ui;

    switch (collection) {
      case 'clients': {
        record = await pb.collection('clients').create(mappers.clientToSchema(value, tenantId));
        ui = mappers.mapClient(record);
        setData((current) => ({ ...current, clients: [...current.clients, ui] }));
        return ui;
      }
      case 'jobs': {
        record = await pb.collection('jobs').create(mappers.jobToSchema(value, tenantId), { expand: 'engineer,property_id' });
        ui = mappers.mapJob(record);
        setData((current) => ({ ...current, jobs: [...current.jobs, ui] }));
        return ui;
      }
      case 'properties': {
        record = await pb.collection('properties').create(mappers.propertyToSchema(value, tenantId), { expand: 'client_id' });
        ui = mappers.mapProperty(record);
        setData((current) => ({ ...current, properties: [...current.properties, ui] }));
        return ui;
      }
      case 'invoices': {
        record = await pb.collection('invoices').create(mappers.invoiceToSchema(value, tenantId));
        ui = mappers.mapInvoice(record);
        setData((current) => ({ ...current, invoices: [...current.invoices, ui] }));
        return ui;
      }
      case 'payments': {
        record = await pb.collection('payments').create(mappers.paymentToSchema(value, tenantId));
        const invoices = data.invoices;
        const invoicesById = Object.fromEntries(invoices.map((inv) => [inv.id, inv]));
        ui = mappers.mapPayment(record, invoicesById);
        setData((current) => ({ ...current, payments: [...current.payments, ui] }));
        return ui;
      }
      case 'keyDates': {
        record = await pb.collection('key_dates').create(mappers.keyDateToSchema(value, tenantId));
        ui = mappers.mapKeyDate(record);
        setData((current) => ({ ...current, keyDates: [...current.keyDates, ui] }));
        return ui;
      }
      case 'messages': {
        record = await pb.collection('messages').create(mappers.messageToSchema(value, tenantId));
        ui = mappers.mapMessage(record);
        // Idempotent append: the realtime SSE "create" event can land
        // before this HTTP response resolves — the same record would be
        // added twice (seen as duplicated messages in chat/inbox).
        setData((current) => ({
          ...current,
          messages: current.messages.some((item) => item.id === ui.id)
            ? current.messages
            : [...current.messages, ui],
        }));
        return ui;
      }
      case 'documents': {
        const form = new FormData();
        form.append('tenant_id', tenantId);
        form.append('client_id', value.client_id);
        if (value.type) form.append('type', value.type);
        if (value.file) form.append('file', value.file);
        record = await pb.collection('documents').create(form);
        ui = mappers.mapDocument(record);
        setData((current) => ({ ...current, documents: [...current.documents, ui] }));
        return ui;
      }
      case 'notification_log': {
        record = await pb.collection('notification_log').create({
          tenant_id: tenantId,
          channel: value.channel || 'telegram',
          recipient: value.recipient || '',
          context: value.context || '',
          status: value.status || 'sent',
          sent_at: value.created_at || new Date().toISOString(),
        });
        ui = mappers.mapNotification(record);
        setData((current) => ({ ...current, notification_log: [...current.notification_log, ui] }));
        return ui;
      }
      case 'notes': {
        record = await pb.collection('notes').create(mappers.noteToSchema(value, tenantId));
        ui = mappers.mapNote(record);
        setData((current) => ({ ...current, notes: [ui, ...current.notes] }));
        return ui;
      }
      case 'quotes': {
        record = await pb.collection('quotes').create(mappers.quoteToSchema(value, tenantId));
        ui = mappers.mapQuote(record);
        setData((current) => ({ ...current, quotes: [ui, ...current.quotes] }));
        return ui;
      }
      case 'certificates': {
        // File upload goes through FormData; expiry-synced key_date is
        // created by the pb_hook (certificates.pb.js). Structured CP12
        // details ride along as JSON. Save-path fix (handoff §2 step 3):
        // form_data / classification / engineer fields / photos are now
        // actually sent — previously dropped silently.
        const form = new FormData();
        form.append('tenant_id', tenantId);
        form.append('client_id', value.client_id);
        // Wizard passes the engineer_job id (canonical schema has both
        // relations; job_id stays for the admin pass-5 flow).
        if (value.job_id) form.append('engineer_job_id', value.job_id);
        if (value.engineer_job_id) form.append('engineer_job_id', value.engineer_job_id);
        form.append('type', value.type || 'other');
        if (value.certificate_number) form.append('certificate_number', value.certificate_number);
        if (value.issued_date) form.append('issued_date', value.issued_date);
        if (value.expiry_date) form.append('expiry_date', value.expiry_date);
        if (value.notes) form.append('notes', value.notes);
        if (value.form_data && Object.keys(value.form_data).length) form.append('form_data', JSON.stringify(value.form_data));
        if (value.details && Object.keys(value.details).length) form.append('details', JSON.stringify(value.details));
        if (value.classification) form.append('classification', value.classification);
        if (value.engineer_id) form.append('engineer_id', value.engineer_id);
        if (value.engineer_signature_id) form.append('engineer_signature_id', value.engineer_signature_id);
        if (value.status) form.append('status', value.status);
        if (value.file) form.append('file', value.file);
        // Photos captured via the device camera are data URLs — convert to
        // File objects so they persist as real files on the photos field.
        if (Array.isArray(value.photos) && value.photos.length > 0) {
          value.photos.forEach((photo, i) => {
            const file = dataUrlToFile(photo.data || photo, `cert_photo_${Date.now()}_${i}.jpg`);
            if (file) form.append('photos', file);
          });
        }
        record = await pb.collection('certificates').create(form);
        ui = mappers.mapCertificate(record);
        setData((current) => ({ ...current, certificates: [...current.certificates, ui] }));
        return ui;
      }
      case 'pricingCatalog': {
        record = await pb.collection('pricing_catalog').create(mappers.pricingItemToSchema(value, tenantId));
        ui = mappers.mapPricingItem(record);
        setData((current) => ({ ...current, pricingCatalog: [...current.pricingCatalog, ui] }));
        return ui;
      }
      case 'team': {
        const password = tempPassword();
        record = await pb.collection('accounts').create({
          name: value.name,
          email: value.email,
          role: value.role || 'agency_staff',
          status: value.status || 'invited',
          tenant_id: tenantId,
          password,
          passwordConfirm: password,
          verified: true,
        });
        ui = { ...mappers.mapTeamMember(record), tempPassword: password };
        setData((current) => ({ ...current, team: [...current.team, ui] }));
        return ui;
      }
      case 'parts': {
        // File uploads (image/datasheet) go through FormData; parts_db
        // files are NOT protected today (TBC-3 note) but the UI still
        // serves them via token URLs.
        const form = new FormData();
        Object.entries(mappers.partToSchema(value, tenantId)).forEach(([key, val]) => {
          if (val !== undefined && val !== null) form.append(key, String(val));
        });
        if (value.image instanceof File) form.append('image', value.image);
        if (value.datasheet instanceof File) form.append('datasheet', value.datasheet);
        record = await pb.collection('parts_db').create(form);
        ui = mappers.mapPart(record);
        setData((current) => ({ ...current, parts: [...current.parts, ui] }));
        return ui;
      }
      case 'faults': {
        record = await pb.collection('faults_db').create(mappers.faultToSchema(value, tenantId));
        ui = mappers.mapFault(record);
        setData((current) => ({ ...current, faults: [...current.faults, ui] }));
        return ui;
      }
      case 'engineerDocs': {
        const form = new FormData();
        Object.entries(mappers.engineerDocToSchema(value, tenantId)).forEach(([key, val]) => {
          if (val !== undefined && val !== null) form.append(key, String(val));
        });
        if (value.file instanceof File) form.append('file', value.file);
        record = await pb.collection('engineer_docs').create(form);
        ui = mappers.mapEngineerDoc(record);
        setData((current) => ({ ...current, engineerDocs: [...current.engineerDocs, ui] }));
        return ui;
      }
      case 'expenses': {
        record = await pb.collection('expenses').create(mappers.expenseToSchema(value, tenantId));
        ui = mappers.mapExpense(record);
        setData((current) => ({ ...current, expenses: [...current.expenses, ui] }));
        return ui;
      }
      case 'lineItemTemplates': {
        // Task 1.5: line-item templates for quick-start invoice items.
        record = await pb.collection('line_item_templates').create(mappers.lineItemTemplateToSchema(value, tenantId));
        ui = mappers.mapLineItemTemplate(record);
        setData((current) => ({ ...current, lineItemTemplates: [...current.lineItemTemplates, ui] }));
        return ui;
      }
      default:
        throw new Error(`add: unknown collection "${collection}"`);
    }
  }, [data]);

  const update = useCallback(async (collection, id, value) => {
    const pb = activePb();
    const tenantId = tenantRef.current.id;
    let record;
    let ui;

    switch (collection) {
      case 'clients': {
        record = await pb.collection('clients').update(id, mappers.clientToSchema(value, tenantId));
        ui = mappers.mapClient(record);
        setData((current) => ({ ...current, clients: current.clients.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'jobs': {
        record = await pb.collection('jobs').update(id, mappers.jobToSchema(value, tenantId), { expand: 'engineer,property_id' });
        ui = mappers.mapJob(record);
        setData((current) => ({ ...current, jobs: current.jobs.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'properties': {
        record = await pb.collection('properties').update(id, mappers.propertyToSchema(value, tenantId), { expand: 'client_id' });
        ui = mappers.mapProperty(record);
        setData((current) => ({ ...current, properties: current.properties.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'invoices': {
        record = await pb.collection('invoices').update(id, mappers.invoiceToSchema(value, tenantId));
        ui = mappers.mapInvoice(record);
        setData((current) => ({ ...current, invoices: current.invoices.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'payments': {
        record = await pb.collection('payments').update(id, mappers.paymentToSchema(value, tenantId));
        const invoicesById = Object.fromEntries(data.invoices.map((inv) => [inv.id, inv]));
        ui = mappers.mapPayment(record, invoicesById);
        setData((current) => ({ ...current, payments: current.payments.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'keyDates': {
        record = await pb.collection('key_dates').update(id, mappers.keyDateToSchema(value, tenantId));
        ui = mappers.mapKeyDate(record);
        setData((current) => ({ ...current, keyDates: current.keyDates.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'messages': {
        // read-state changes go through portal-api (§6.3) — see MessagesInbox;
        // a direct update here is still allowed for agency roles.
        record = await pb.collection('messages').update(id, mappers.messageToSchema(value, tenantId));
        ui = mappers.mapMessage(record);
        setData((current) => ({ ...current, messages: current.messages.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'notification_log': {
        record = await pb.collection('notification_log').update(id, {
          channel: value.channel,
          recipient: value.recipient,
          context: value.context,
          status: value.status,
        });
        ui = mappers.mapNotification(record);
        setData((current) => ({ ...current, notification_log: current.notification_log.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'documents': {
        // Admin document ops (spec §2.5/§3): replace (file → version bump
        // by audit.pb.js) and archive (soft-delete). Multipart because the
        // collection carries a protected file field.
        const form = new FormData();
        form.append('tenant_id', tenantId);
        form.append('client_id', value.client_id);
        if (value.type) form.append('type', value.type);
        if ('archived' in value) form.append('archived', value.archived ? 'true' : 'false');
        if (value.file instanceof File) form.append('file', value.file);
        record = await pb.collection('documents').update(id, form);
        ui = mappers.mapDocument(record);
        setData((current) => ({ ...current, documents: current.documents.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'notes': {
        record = await pb.collection('notes').update(id, mappers.noteToSchema(value, tenantId));
        ui = mappers.mapNote(record);
        setData((current) => ({ ...current, notes: current.notes.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'quotes': {
        // Agency edits (send/decline/expire/line items). Client approvals
        // also land here — the pb_hook clamps their writable fields.
        record = await pb.collection('quotes').update(id, mappers.quoteToSchema(value, tenantId));
        ui = mappers.mapQuote(record);
        setData((current) => ({ ...current, quotes: current.quotes.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'certificates': {
        const form = new FormData();
        form.append('type', value.type);
        if (value.certificate_number) form.append('certificate_number', value.certificate_number);
        if (value.issued_date) form.append('issued_date', value.issued_date || null);
        if (value.expiry_date) form.append('expiry_date', value.expiry_date || null);
        if (value.notes) form.append('notes', value.notes || '');
        if (value.form_data && Object.keys(value.form_data).length) form.append('form_data', JSON.stringify(value.form_data));
        if (value.details && Object.keys(value.details).length) form.append('details', JSON.stringify(value.details));
        if (value.classification) form.append('classification', value.classification);
        if (value.engineer_id) form.append('engineer_id', value.engineer_id);
        if (value.engineer_signature_id) form.append('engineer_signature_id', value.engineer_signature_id);
        if (value.status) form.append('status', value.status);
        if (Array.isArray(value.photos) && value.photos.length > 0) {
          value.photos.forEach((photo, i) => {
            const file = dataUrlToFile(photo.data || photo, `cert_photo_${Date.now()}_${i}.jpg`);
            if (file) form.append('photos', file);
          });
        }
        record = await pb.collection('certificates').update(id, form);
        ui = mappers.mapCertificate(record);
        setData((current) => ({ ...current, certificates: current.certificates.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'pricingCatalog': {
        record = await pb.collection('pricing_catalog').update(id, mappers.pricingItemToSchema(value, tenantId));
        ui = mappers.mapPricingItem(record);
        setData((current) => ({ ...current, pricingCatalog: current.pricingCatalog.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'parts': {
        const form = new FormData();
        Object.entries(mappers.partToSchema(value, tenantId)).forEach(([key, val]) => {
          if (val !== undefined && val !== null) form.append(key, String(val));
        });
        if (value.image instanceof File) form.append('image', value.image);
        if (value.datasheet instanceof File) form.append('datasheet', value.datasheet);
        record = await pb.collection('parts_db').update(id, form);
        ui = mappers.mapPart(record);
        setData((current) => ({ ...current, parts: current.parts.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'faults': {
        record = await pb.collection('faults_db').update(id, mappers.faultToSchema(value, tenantId));
        ui = mappers.mapFault(record);
        setData((current) => ({ ...current, faults: current.faults.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'engineerDocs': {
        const form = new FormData();
        Object.entries(mappers.engineerDocToSchema(value, tenantId)).forEach(([key, val]) => {
          if (val !== undefined && val !== null) form.append(key, String(val));
        });
        if (value.file instanceof File) form.append('file', value.file);
        record = await pb.collection('engineer_docs').update(id, form);
        ui = mappers.mapEngineerDoc(record);
        setData((current) => ({ ...current, engineerDocs: current.engineerDocs.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'expenses': {
        record = await pb.collection('expenses').update(id, mappers.expenseToSchema(value, tenantId));
        ui = mappers.mapExpense(record);
        setData((current) => ({ ...current, expenses: current.expenses.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'signups': {
        // Lead pipeline status change only (new → contacted → converted |
        // declined). Rows are created exclusively by portal-api (superuser)
        // from the public /sign-up form.
        record = await pb.collection('signups').update(id, { status: value.status });
        ui = mappers.mapSignup(record);
        setData((current) => ({ ...current, signups: current.signups.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'lineItemTemplates': {
        // Task 1.5: line-item templates for quick-start invoice items.
        record = await pb.collection('line_item_templates').update(id, mappers.lineItemTemplateToSchema(value, tenantId));
        ui = mappers.mapLineItemTemplate(record);
        setData((current) => ({ ...current, lineItemTemplates: current.lineItemTemplates.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      case 'engineerJobSheets': {
        // Admin review path: notes always; status/completed_at only when the
        // office explicitly re-opens a completed sheet for amendments. Never
        // clobber engineer-owned form fields.
        const patch = { admin_notes: value.admin_notes || '' };
        if (value.status) patch.status = value.status;
        if ('completed_at' in value) patch.completed_at = value.completed_at;
        record = await pb.collection('engineer_job_sheets').update(id, patch);
        ui = mappers.mapEngineerJobSheet(record);
        setData((current) => ({ ...current, engineerJobSheets: current.engineerJobSheets.map((item) => (item.id === id ? ui : item)) }));
        return ui;
      }
      default:
        throw new Error(`update: unknown collection "${collection}"`);
    }
  }, [data]);

  const remove = useCallback(async (collection, id) => {
    const pb = activePb();
    const names = {
      clients: 'clients',
      jobs: 'jobs',
      properties: 'properties',
      invoices: 'invoices',
      payments: 'payments',
      keyDates: 'key_dates',
      messages: 'messages',
      documents: 'documents',
      notification_log: 'notification_log',
      team: 'accounts',
      notes: 'notes',
      quotes: 'quotes',
      certificates: 'certificates',
      pricingCatalog: 'pricing_catalog',
      reviewRequests: 'review_requests',
      parts: 'parts_db',
      faults: 'faults_db',
      engineerDocs: 'engineer_docs',
      expenses: 'expenses',
      lineItemTemplates: 'line_item_templates',
    };
    const name = names[collection];
    if (!name) throw new Error(`remove: unknown collection "${collection}"`);
    await pb.collection(name).delete(id);
    setData((current) => ({ ...current, [collection]: current[collection].filter((item) => item.id !== id) }));
  }, []);

  // Generic local-only patch (no network) — used after API-routed writes
  const patchLocal = useCallback((collection, id, value) => {
    setData((current) => ({
      ...current,
      [collection]: current[collection].map((item) => (item.id === id ? { ...item, ...value } : item)),
    }));
  }, []);

  // settings lives on the tenants record; persist the diff and remap
  const updateCollection = useCallback(async (collection, updater) => {
    if (collection !== 'settings') {
      setData((current) => ({
        ...current,
        [collection]: typeof updater === 'function' ? updater(current[collection]) : updater,
      }));
      return null;
    }

    const nextSettings = typeof updater === 'function' ? updater(data.settings) : updater;
    const patch = mappers.settingsToTenantPatch(nextSettings);
    const record = await activePb().collection('tenants').update(tenantRef.current.id, patch);
    const tenantUi = mappers.mapTenant(record);
    tenantRef.current = tenantUi;
    setData((current) => ({
      ...current,
      tenant: tenantUi,
      settings: mappers.buildSettings(tenantUi),
    }));
    applyBranding(tenantUi);
    return tenantUi;
  }, [data, applyBranding]);

  const refresh = useCallback(async () => {
    await load();
  }, [load]);

  const value = useMemo(() => {
    const role = activePb().authStore.record?.role || null;
    const isAgency = role === 'agency_admin' || role === 'agency_staff';
    return {
      data,
      ready,
      error,
      isAgency,
      add,
      update,
      remove,
      updateCollection,
      patchLocal,
      refresh,
    };
  }, [data, ready, error, add, update, remove, updateCollection, patchLocal, refresh]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export function useData() {
  return useContext(DataContext);
}
